import React from 'react';
import {
    FaUsers,
    FaRetweet,
    FaMoneyBillWave,
    FaBullhorn,
    FaStar,
    FaChartLine,
    FaEye,
    FaArrowUp,
    FaArrowDown,
    FaClock,
    FaGift,
    FaHeart,
    FaThumbsUp,
    FaThumbsDown,
    FaMinus,
    FaPercentage,
    FaCalendarAlt,
    FaFilter,
    FaDownload
} from 'react-icons/fa';

// Componente base para métricas
export const MetricCard = ({ title, value, change, icon, tooltip, color = 'blue', trend, trendValue }) => {
    const colorClasses = {
        blue: 'text-blue-500 bg-blue-50',
        purple: 'text-purple-500 bg-purple-50',
        green: 'text-green-500 bg-green-50',
        yellow: 'text-yellow-500 bg-yellow-50',
        orange: 'text-orange-500 bg-orange-50',
        pink: 'text-pink-500 bg-pink-50',
        indigo: 'text-indigo-500 bg-indigo-50',
        teal: 'text-teal-500 bg-teal-50',
        red: 'text-red-500 bg-red-50',
        gray: 'text-gray-500 bg-gray-50'
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div className={`p-3 rounded-full ${colorClasses[color]}`}>
                        {icon}
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">{title}</p>
                        <p className="text-2xl font-bold text-gray-900">{value}</p>
                    </div>
                </div>
                {trend && (
                    <div className={`flex items-center text-sm ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600'
                        }`}>
                        {trend === 'up' ? <FaArrowUp /> : trend === 'down' ? <FaArrowDown /> : <FaMinus />}
                        <span className="ml-1">{trendValue}</span>
                    </div>
                )}
            </div>
            {tooltip && (
                <p className="text-xs text-gray-500 mt-2">{tooltip}</p>
            )}
        </div>
    );
};

// Componente para análisis de cohortes
export const CohortAnalysis = ({ data }) => {
    if (!data || !data.cohorts) return null;

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaChartLine className="mr-3 text-indigo-500" />
                Análisis de Cohortes
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <MetricCard
                    title="Retención 1 Mes"
                    value={`${data.retention1Month || 0}%`}
                    icon={<FaCalendarAlt className="text-indigo-500" />}
                    color="indigo"
                    tooltip="Porcentaje de clientes que regresan en 1 mes"
                />
                <MetricCard
                    title="Retención 3 Meses"
                    value={`${data.retention3Months || 0}%`}
                    icon={<FaCalendarAlt className="text-purple-500" />}
                    color="purple"
                    tooltip="Porcentaje de clientes que regresan en 3 meses"
                />
                <MetricCard
                    title="Retención 6 Meses"
                    value={`${data.retention6Months || 0}%`}
                    icon={<FaCalendarAlt className="text-green-500" />}
                    color="green"
                    tooltip="Porcentaje de clientes que regresan en 6 meses"
                />
                <MetricCard
                    title="Valor de Vida"
                    value={`€${data.lifetimeValue || 0}`}
                    icon={<FaMoneyBillWave className="text-yellow-500" />}
                    color="yellow"
                    tooltip="Valor promedio de vida del cliente"
                />
            </div>

            {/* Tabla de cohortes */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Cohorte
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Clientes
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                1 Mes
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                3 Meses
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                6 Meses
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                12 Meses
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {data.cohorts?.map((cohort, index) => (
                            <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {cohort.period}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cohort.customers}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cohort.retention1Month}%
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cohort.retention3Months}%
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cohort.retention6Months}%
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cohort.retention12Months}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Componente para embudo de leads
export const LeadsFunnel = ({ data }) => {
    if (!data || !data.funnel) return null;

    const stages = [
        { name: 'Conciencia', value: data.funnel.awareness || 0, color: 'blue' },
        { name: 'Interés', value: data.funnel.interest || 0, color: 'purple' },
        { name: 'Consideración', value: data.funnel.consideration || 0, color: 'green' },
        { name: 'Intención', value: data.funnel.intent || 0, color: 'yellow' },
        { name: 'Compra', value: data.funnel.purchase || 0, color: 'orange' }
    ];

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaEye className="mr-3 text-teal-500" />
                Embudo de Conversión
            </h3>

            <div className="space-y-4">
                {stages.map((stage, index) => {
                    const previousValue = index > 0 ? stages[index - 1].value : stage.value;
                    const conversionRate = previousValue > 0 ? (stage.value / previousValue * 100) : 0;

                    return (
                        <div key={index} className="relative">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                                <div className="flex items-center space-x-4">
                                    <span className="text-sm text-gray-500">{stage.value} leads</span>
                                    {index > 0 && (
                                        <span className="text-xs text-gray-400">
                                            {conversionRate.toFixed(1)}% conversión
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div
                                    className={`bg-${stage.color}-500 h-4 rounded-full transition-all duration-500`}
                                    style={{ width: `${(stage.value / stages[0].value) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Métricas de conversión */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <MetricCard
                    title="Tasa de Conversión Total"
                    value={`${data.totalConversionRate || 0}%`}
                    icon={<FaPercentage className="text-teal-500" />}
                    color="teal"
                    tooltip="Porcentaje de leads que completan todo el embudo"
                />
                <MetricCard
                    title="Costo por Lead"
                    value={`€${data.costPerLead || 0}`}
                    icon={<FaMoneyBillWave className="text-green-500" />}
                    color="green"
                    tooltip="Costo promedio por lead generado"
                />
                <MetricCard
                    title="Tiempo de Conversión"
                    value={`${data.conversionTime || 0} días`}
                    icon={<FaClock className="text-orange-500" />}
                    color="orange"
                    tooltip="Tiempo promedio para completar la conversión"
                />
            </div>
        </div>
    );
};

// Componente para análisis de sentimiento
export const SentimentAnalysis = ({ data }) => {
    if (!data || !data.sentiment) return null;

    const sentimentData = data.sentiment;
    const total = sentimentData.positive + sentimentData.neutral + sentimentData.negative;

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaStar className="mr-3 text-pink-500" />
                Análisis de Sentimiento
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <MetricCard
                    title="Positivo"
                    value={`${sentimentData.positive || 0}`}
                    icon={<FaThumbsUp className="text-green-500" />}
                    color="green"
                    tooltip={`${total > 0 ? ((sentimentData.positive / total) * 100).toFixed(1) : 0}% de las reseñas`}
                />
                <MetricCard
                    title="Neutral"
                    value={`${sentimentData.neutral || 0}`}
                    icon={<FaMinus className="text-gray-500" />}
                    color="gray"
                    tooltip={`${total > 0 ? ((sentimentData.neutral / total) * 100).toFixed(1) : 0}% de las reseñas`}
                />
                <MetricCard
                    title="Negativo"
                    value={`${sentimentData.negative || 0}`}
                    icon={<FaThumbsDown className="text-red-500" />}
                    color="red"
                    tooltip={`${total > 0 ? ((sentimentData.negative / total) * 100).toFixed(1) : 0}% de las reseñas`}
                />
            </div>

            {/* Gráfica de sentimiento */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700">Distribución de Sentimiento</h4>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Positivo</span>
                        <span className="text-sm font-medium text-gray-900">
                            {total > 0 ? ((sentimentData.positive / total) * 100).toFixed(1) : 0}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className="bg-green-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${total > 0 ? (sentimentData.positive / total) * 100 : 0}%` }}
                        ></div>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Neutral</span>
                        <span className="text-sm font-medium text-gray-900">
                            {total > 0 ? ((sentimentData.neutral / total) * 100).toFixed(1) : 0}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className="bg-gray-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${total > 0 ? (sentimentData.neutral / total) * 100 : 0}%` }}
                        ></div>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Negativo</span>
                        <span className="text-sm font-medium text-gray-900">
                            {total > 0 ? ((sentimentData.negative / total) * 100).toFixed(1) : 0}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className="bg-red-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${total > 0 ? (sentimentData.negative / total) * 100 : 0}%` }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente para métricas de productos
export const ProductMetrics = ({ data }) => {
    if (!data || !data.products) return null;

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaMoneyBillWave className="mr-3 text-yellow-500" />
                Análisis de Productos
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <MetricCard
                    title="Producto Más Vendido"
                    value={data.topProduct?.name || 'N/A'}
                    icon={<FaArrowUp className="text-green-500" />}
                    color="green"
                    tooltip={`${data.topProduct?.sales || 0} unidades vendidas`}
                />
                <MetricCard
                    title="Ticket Promedio"
                    value={`€${data.averageTicket || 0}`}
                    icon={<FaMoneyBillWave className="text-blue-500" />}
                    color="blue"
                    tooltip="Valor promedio por transacción"
                />
                <MetricCard
                    title="Día Pico"
                    value={data.peakDay || 'N/A'}
                    icon={<FaCalendarAlt className="text-purple-500" />}
                    color="purple"
                    tooltip="Día de la semana con más ventas"
                />
                <MetricCard
                    title="Hora Pico"
                    value={data.peakHour || 'N/A'}
                    icon={<FaClock className="text-orange-500" />}
                    color="orange"
                    tooltip="Hora del día con más ventas"
                />
            </div>

            {/* Tabla de productos */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Producto
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ventas
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ingresos
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                % del Total
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {data.products?.map((product, index) => (
                            <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {product.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {product.sales}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    €{product.revenue}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {product.percentage}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Componente para fuentes de captación
export const AcquisitionSources = ({ data }) => {
    if (!data || !data.sources) return null;

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaUsers className="mr-3 text-green-500" />
                Fuentes de Captación
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <MetricCard
                    title="Nuevos Clientes"
                    value={data.newCustomers || 0}
                    icon={<FaUsers className="text-green-500" />}
                    color="green"
                    tooltip="Total de nuevos clientes en el período"
                />
                <MetricCard
                    title="Fuente Principal"
                    value={data.topSource?.name || 'N/A'}
                    icon={<FaArrowUp className="text-blue-500" />}
                    color="blue"
                    tooltip={`${data.topSource?.percentage || 0}% del total`}
                />
                <MetricCard
                    title="Costo por Lead"
                    value={`€${data.costPerLead || 0}`}
                    icon={<FaMoneyBillWave className="text-yellow-500" />}
                    color="yellow"
                    tooltip="Costo promedio por lead generado"
                />
                <MetricCard
                    title="Conversión"
                    value={`${data.conversionRate || 0}%`}
                    icon={<FaPercentage className="text-purple-500" />}
                    color="purple"
                    tooltip="Tasa de conversión a primera compra"
                />
            </div>

            {/* Gráfica de fuentes */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700">Distribución por Fuente</h4>
                {data.sources?.map((source, index) => (
                    <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">{source.name}</span>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">{source.leads} leads</span>
                                <span className="text-xs text-gray-500">({source.percentage}%)</span>
                            </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${source.percentage}%` }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default {
    MetricCard,
    CohortAnalysis,
    LeadsFunnel,
    SentimentAnalysis,
    ProductMetrics,
    AcquisitionSources
};
